import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import dayjs from 'dayjs';
import { Button, Segmented, Select, Spin, Tooltip, Typography } from 'antd';
import { ArrowRightOutlined, HistoryOutlined, ReloadOutlined, WarningFilled } from '@ant-design/icons';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { StatusPill, type PillTone } from '@/components/StatusPill';
import { colors } from '@/theme/tokens';
import {
  getWorkflowRuns,
  type RunStatus,
  type StepStatus,
  type WorkflowRunHistory,
  type WorkflowRunStepSpan,
  type WorkflowRunTrace,
} from '@/api/workflowRuns';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// WorkflowRuns — the Runs tab: how this workflow has actually been running
// ─────────────────────────────────────────────────────────────────────────────
//
//   Top: the window's counts (every run) and medians (the latest traced runs).
//   Middle: each step with its median time and failures — the slow or failing step stands out.
//   Bottom: one run as a timeline — a bar per step from the end of the steps before it to its own checkpoint,
//   the step's own log lines under its bar, the run's own lines (queued / completed / failure) at the end.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  workflowName: string;
  isDark: boolean;
}

const WINDOWS = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
];

export function WorkflowRuns({ workflowName, isDark }: Props) {
  const [days, setDays] = useState(30);
  const [history, setHistory] = useState<WorkflowRunHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getWorkflowRuns(workflowName, days);
      setHistory(data);
      setSelectedKey((current) => (current && data?.recent.some((r) => r.key === current) ? current : data?.recent[0]?.key ?? null));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load runs');
    } finally {
      setLoading(false);
    }
  }, [workflowName, days]);

  useEffect(() => {
    void load();
  }, [load]);

  const ink = palette(isDark);
  const selected = history?.recent.find((r) => r.key === selectedKey) ?? null;
  const titleOf = useMemo(() => new Map((history?.steps ?? []).map((s) => [s.stepId, s.title])), [history]);
  const agentSteps = useMemo(() => new Set((history?.steps ?? []).filter((s) => s.isAgent).map((s) => s.stepId)), [history]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Segmented options={WINDOWS} value={days} onChange={(v) => setDays(Number(v))} size="small" />
        <Button size="small" icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>
          Refresh
        </Button>
        {history && history.runs > 0 && (
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 'auto' }}>
            Counts cover every run since {dayjs(history.sinceUtc).format('D MMM')}. Times and steps come from the latest{' '}
            {history.tracedRuns} {history.tracedRuns === 1 ? 'run' : 'runs'}.
          </Text>
        )}
      </div>

      {error && <Text type="danger">{error}</Text>}

      {loading && !history && (
        <div style={{ minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin />
        </div>
      )}

      {history && history.runs === 0 && !loading && (
        <BrandedEmpty
          icon={<HistoryOutlined />}
          title={`No runs in the last ${history.days} days`}
          description="Runs appear here as soon as this workflow is started for the selected practice."
          size="sm"
        />
      )}

      {history && history.runs > 0 && (
        <Spin spinning={loading}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Totals history={history} ink={ink} />
            <StepStrip history={history} ink={ink} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <Text strong>Run</Text>
              <Select
                size="small"
                style={{ minWidth: 340 }}
                value={selectedKey ?? undefined}
                onChange={setSelectedKey}
                options={history.recent.map((r) => ({
                  value: r.key,
                  label: `${dayjs(r.createdUtc).format('D MMM, HH:mm')} · ${RUN_STATUS[r.status].label}${r.durationSeconds != null ? ` · ${duration(r.durationSeconds)}` : ''}`,
                }))}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {history.recent.length} most recent
              </Text>
            </div>
            {selected && <RunTimeline run={selected} titleOf={titleOf} agentSteps={agentSteps} ink={ink} />}
          </div>
        </Spin>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Totals
// ────────────────────────────────────────────────────────────────────────

function Totals({ history, ink }: { history: WorkflowRunHistory; ink: Ink }) {
  const finishedShare = history.runs > 0 ? Math.round((history.finished / history.runs) * 100) : 0;
  const queue = history.medianQueueSeconds ?? 0;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
      <Tile
        ink={ink}
        label="Runs"
        value={history.runs.toLocaleString()}
        hint={`${history.finished.toLocaleString()} finished · ${history.failed.toLocaleString()} failed${history.unfinished > 0 ? ` · ${history.unfinished.toLocaleString()} not finished` : ''}${history.capped ? ' · at least' : ''}`}
      />
      <Tile ink={ink} label="Finished" value={`${finishedShare}%`} hint={history.failed > 0 ? `${history.failed.toLocaleString()} failed` : 'none failed'} />
      <Tile
        ink={ink}
        label="Median time"
        value={history.medianRunSeconds != null ? duration(history.medianRunSeconds) : '—'}
        hint={queue >= 1 ? `plus ${duration(queue)} waiting in the queue` : 'start to finish, finished runs'}
      />
      <Tile
        ink={ink}
        label="Tokens per run"
        value={history.medianTokens != null ? compact(history.medianTokens) : '—'}
        hint={history.runsWithTokens > 0 ? `median of ${history.runsWithTokens.toLocaleString()} runs that recorded tokens` : 'no run recorded tokens'}
      />
    </div>
  );
}

function Tile({ label, value, hint, ink }: { label: string; value: string; hint: string; ink: Ink }) {
  return (
    <div style={{ background: ink.tile, borderRadius: 10, padding: '10px 14px' }}>
      <div style={{ fontSize: 12, color: ink.muted }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color: ink.text, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      <div style={{ fontSize: 11.5, color: ink.muted, marginTop: 2 }}>{hint}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Steps across the traced runs
// ────────────────────────────────────────────────────────────────────────

function StepStrip({ history, ink }: { history: WorkflowRunHistory; ink: Ink }) {
  const slowest = history.steps.reduce<string | null>((best, s) => {
    if (s.medianSeconds == null) return best;
    const bestValue = history.steps.find((x) => x.stepId === best)?.medianSeconds ?? -1;
    return s.medianSeconds > bestValue ? s.stepId : best;
  }, null);

  return (
    <div style={{ display: 'flex', alignItems: 'stretch', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
      {history.steps.map((s, i) => (
        <div key={s.stepId} style={{ display: 'flex', alignItems: 'stretch', gap: 6 }}>
          {i > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', color: ink.faint }}>
              <ArrowRightOutlined />
            </div>
          )}
          <div
            style={{
              minWidth: 180,
              maxWidth: 260,
              borderRadius: 10,
              padding: '8px 12px',
              border: `1px solid ${s.isAgent ? ink.aiBorder : ink.border}`,
              background: s.isAgent ? ink.aiBg : ink.card,
            }}
          >
            <Tooltip title={s.stepId}>
              <div style={{ fontWeight: 600, fontSize: 13, color: s.isAgent ? ink.aiText : ink.text }}>{s.title}</div>
            </Tooltip>
            <div style={{ fontSize: 12, color: s.isAgent ? ink.aiText : ink.muted }}>
              {s.medianSeconds != null ? `median ${duration(s.medianSeconds)}` : 'no completed runs traced'}
              {slowest === s.stepId && history.steps.length > 1 ? ', slowest step' : ''}
            </div>
            <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <StatusPill tone={s.failed > 0 ? 'red' : 'green'}>{s.failed > 0 ? `${s.failed} failed` : '0 failed'}</StatusPill>
              <StatusPill tone="slate">ran in {s.ran}</StatusPill>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// One run as a timeline
// ────────────────────────────────────────────────────────────────────────

const RUN_STATUS: Record<RunStatus, { label: string; tone: PillTone }> = {
  completed: { label: 'Finished', tone: 'green' },
  failed: { label: 'Failed', tone: 'red' },
  running: { label: 'Running', tone: 'ink' },
  queued: { label: 'Queued', tone: 'slate' },
  'awaiting-input': { label: 'Waiting for input', tone: 'amber' },
};

const STEP_STATUS_WORDS: Partial<Record<StepStatus, string>> = {
  'not-reached': 'not reached',
  'not-recorded': 'not recorded: this run is older than step checkpoints',
  'not-in-version': 'not in the version this run used',
  running: 'running…',
  waiting: 'waiting for input',
};

function RunTimeline({
  run,
  titleOf,
  agentSteps,
  ink,
}: {
  run: WorkflowRunTrace;
  titleOf: Map<string, string>;
  agentSteps: Set<string>;
  ink: Ink;
}) {
  const ends = run.steps.map((s) => s.endSeconds ?? s.startSeconds ?? 0);
  const span = Math.max(run.durationSeconds ?? 0, ...ends, 1);
  const pct = (seconds: number) => `${Math.min(100, Math.max(0, (seconds / span) * 100))}%`;
  const status = RUN_STATUS[run.status];

  return (
    <div style={{ border: `1px solid ${ink.border}`, borderRadius: 12, padding: '12px 16px', background: ink.card }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{ fontWeight: 600, fontSize: 14, color: ink.text }}>{dayjs(run.createdUtc).format('D MMM YYYY, HH:mm:ss')}</span>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {run.title}
          {run.durationSeconds != null ? ` · took ${duration(run.durationSeconds)}` : ''}
          {(run.queueSeconds ?? 0) >= 1 ? ` · waited ${duration(run.queueSeconds ?? 0)} in the queue` : ''}
        </Text>
        <span style={{ marginLeft: 'auto' }}>
          <StatusPill tone={status.tone}>{status.label}</StatusPill>
        </span>
      </div>

      {run.ranEarlierVersion && (
        <div style={{ ...noteStyle(ink), marginBottom: 12 }}>
          This run used an earlier version of the workflow. Steps it had that today's version does not are marked “earlier version”.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(140px, 220px) minmax(0, 1fr)', columnGap: 14, rowGap: 4, alignItems: 'center' }}>
        {run.steps.map((s) => (
          <StepRow key={s.stepId} step={s} title={titleOf.get(s.stepId) ?? s.stepId} isAgent={agentSteps.has(s.stepId)} pct={pct} ink={ink} />
        ))}
        <div />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: ink.faint, borderTop: `1px solid ${ink.border}`, paddingTop: 4 }}>
          <span>0 s</span>
          <span>{duration(span / 2)}</span>
          <span>{duration(span)}</span>
        </div>
      </div>

      {run.failure && (
        <div style={{ marginTop: 12, fontSize: 12.5, color: colors.red[600], display: 'flex', gap: 8 }}>
          <WarningFilled style={{ marginTop: 3 }} />
          <span>{run.failure}</span>
        </div>
      )}

      <div style={{ marginTop: 12, display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: ink.muted }}>
        <span>
          Tokens: {run.inputTokens.toLocaleString()} in, {run.outputTokens.toLocaleString()} out
          {run.totalTokens > 0 ? ` (${run.totalTokens.toLocaleString()} total)` : ''}
        </span>
        <span>Key {run.key}</span>
      </div>

      {run.lines.length > 0 && (
        <details style={{ marginTop: 10 }}>
          <summary style={{ cursor: 'pointer', fontSize: 12, color: ink.muted }}>Run log ({run.lines.length} lines no step wrote)</summary>
          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {run.lines.map((l, i) => (
              <span key={i} style={{ fontSize: 12, color: ink.muted }}>
                <span style={{ fontVariantNumeric: 'tabular-nums', display: 'inline-block', minWidth: 64 }}>{duration(l.atSeconds)}</span>
                {l.text}
              </span>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function StepRow({
  step,
  title,
  isAgent,
  pct,
  ink,
}: {
  step: WorkflowRunStepSpan;
  title: string;
  isAgent: boolean;
  pct: (s: number) => string;
  ink: Ink;
}) {
  const hasBar = step.startSeconds != null;
  const start = step.startSeconds ?? 0;
  const end = step.endSeconds ?? start;
  const length = Math.max(end - start, 0);
  const color = step.status === 'failed' ? colors.red[500] : step.status === 'running' || step.status === 'waiting' ? colors.ink[500] : isAgent ? colors.violet[500] : colors.slate[400];
  const words = STEP_STATUS_WORDS[step.status];
  const label = step.status === 'completed' || step.status === 'failed' ? duration(length) : words;

  return (
    <>
      <div style={{ fontSize: 13, fontWeight: 600, color: isAgent ? ink.aiText : ink.text, minWidth: 0 }}>
        <Tooltip title={step.stepId}>
          <span>{title}</span>
        </Tooltip>
        {!step.inCurrentDefinition && <div style={{ fontSize: 11, fontWeight: 400, color: ink.muted }}>earlier version</div>}
        {step.completions > 1 && <div style={{ fontSize: 11, fontWeight: 400, color: colors.amber[600] }}>ran {step.completions} times</div>}
      </div>
      <div style={{ position: 'relative', height: 22, background: ink.track, borderRadius: 4 }}>
        {hasBar && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              height: 22,
              left: pct(start),
              width: `max(3px, calc(${pct(end)} - ${pct(start)}))`,
              background: color,
              borderRadius: 4,
              opacity: step.status === 'running' || step.status === 'waiting' ? 0.6 : 1,
            }}
          />
        )}
        <span
          style={{
            position: 'absolute',
            top: 3,
            fontSize: 12,
            whiteSpace: 'nowrap',
            color: hasBar ? ink.text : ink.muted,
            ...(hasBar ? barLabelPosition(start, end, pct) : { left: 8 }),
          }}
        >
          {label}
        </span>
      </div>
      {(step.lines.length > 0 || step.error) && (
        <>
          <div />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1, marginBottom: 6 }}>
            {step.error && <span style={{ fontSize: 12, color: colors.red[600] }}>{step.error}</span>}
            {step.lines.map((l, i) => (
              <span key={i} style={{ fontSize: 12, color: ink.muted }}>
                {l.text}
              </span>
            ))}
          </div>
        </>
      )}
    </>
  );
}

/** The duration label sits just after its bar, or just before it when the bar ends near the right edge. */
function barLabelPosition(start: number, end: number, pct: (s: number) => string): CSSProperties {
  const endShare = parseFloat(pct(end));
  return endShare > 85 ? { right: `calc(100% - ${pct(start)} + 6px)` } : { left: `calc(${pct(end)} + 6px)` };
}

// ────────────────────────────────────────────────────────────────────────
// Formatting + palette
// ────────────────────────────────────────────────────────────────────────

export function duration(seconds: number): string {
  if (!Number.isFinite(seconds)) return '—';
  if (seconds < 10) return `${seconds.toFixed(1)} s`;
  if (seconds < 90) return `${Math.round(seconds)} s`;
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds - m * 60);
    return s > 0 ? `${m} min ${s} s` : `${m} min`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds - h * 3600) / 60);
  return m > 0 ? `${h} h ${m} min` : `${h} h`;
}

function compact(n: number): string {
  return n >= 10_000 ? `${(n / 1000).toFixed(1)}k` : n.toLocaleString();
}

interface Ink {
  text: string;
  muted: string;
  faint: string;
  border: string;
  card: string;
  tile: string;
  track: string;
  aiBg: string;
  aiBorder: string;
  aiText: string;
}

function palette(isDark: boolean): Ink {
  return isDark
    ? {
        text: colors.slate[50],
        muted: colors.slate[400],
        faint: colors.slate[500],
        border: colors.slate[700],
        card: colors.slate[800],
        tile: colors.slate[800],
        track: colors.slate[900],
        aiBg: colors.violet[700] + '33',
        aiBorder: colors.violet[500] + '99',
        aiText: colors.violet[100],
      }
    : {
        text: colors.slate[900],
        muted: colors.slate[500],
        faint: colors.slate[400],
        border: colors.slate[200],
        card: '#FFFFFF',
        tile: colors.slate[50],
        track: colors.slate[100],
        aiBg: colors.violet[50],
        aiBorder: colors.violet[200],
        aiText: colors.violet[700],
      };
}

function noteStyle(ink: Ink): CSSProperties {
  return { fontSize: 12.5, color: ink.muted, background: ink.tile, borderRadius: 8, padding: '8px 12px' };
}
